/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Nav icon registry (#683). One kebab-case name → one Lucide icon, shared by
 * every nav surface — top bar, contextual sidebar, mobile drawer — so an item
 * cannot be drawn with different icons depending on where it appears.
 *
 * Names come from `NAV_STRUCTURE` (`icon` / `tabIcon`); an unknown name falls
 * back to a dot rather than throwing, so adding a nav item can never blank the
 * shell.
 */

import { createElement } from 'react';
import {
  Activity,
  AlertTriangle,
  Archive,
  BarChart3,
  Bell,
  BookOpen,
  Braces,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Circle,
  ClipboardList,
  Code,
  Database,
  Download,
  FileText,
  Filter,
  GitBranch,
  GitCommit,
  LayoutGrid,
  Layers,
  LifeBuoy,
  Link as LinkIcon,
  Lock,
  Menu,
  Network,
  Play,
  Radio,
  RefreshCw,
  Search,
  Settings,
  SlidersHorizontal,
  Terminal,
  TrendingUp,
  Users,
  X,
  Zap,
  type LucideIcon,
  type LucideProps,
} from 'lucide-react';

const NAV_ICONS: Record<string, LucideIcon> = {
  // Workflow-group tabs
  grid: LayoutGrid,
  play: Play,
  sliders: SlidersHorizontal,
  'book-open': BookOpen,
  activity: Activity,
  'bar-chart': BarChart3,
  settings: Settings,
  code: Code,
  // Surfaces
  'alert-triangle': AlertTriangle,
  archive: Archive,
  bell: Bell,
  braces: Braces,
  clipboard: ClipboardList,
  database: Database,
  download: Download,
  'file-text': FileText,
  filter: Filter,
  'git-branch': GitBranch,
  'git-commit': GitCommit,
  layers: Layers,
  'life-buoy': LifeBuoy,
  link: LinkIcon,
  network: Network,
  radio: Radio,
  refresh: RefreshCw,
  search: Search,
  terminal: Terminal,
  'trending-up': TrendingUp,
  users: Users,
  zap: Zap,
  // Shell chrome
  'chevron-right': ChevronRight,
  'chevrons-left': ChevronsLeft,
  'chevrons-right': ChevronsRight,
  lock: Lock,
  menu: Menu,
  x: X,
};

/** Render a nav icon by its kebab-case name. */
export function NavIcon({ name, ...props }: { name: string } & LucideProps) {
  return createElement(NAV_ICONS[name] ?? Circle, {
    size: 16,
    strokeWidth: 2,
    style: { flexShrink: 0 },
    ...props,
  });
}
